/** Tells a school it is running out of AI credits, before it runs out.
 *
 * Until now the first anyone knew was a student being refused mid-exercise,
 * with the message "Not enough AI credits: 0 left, 1 needed" — true, and
 * useless. This warns the admin at 80% and, at zero, tells each role what
 * they can actually do about it.
 *
 * Who sees what matters here. Credits belong to the SCHOOL, so a student
 * cannot buy more and must not be sent to a shop they'd bounce off; they are
 * told to ask their teacher. An admin — including a solo learner, who is
 * their own admin — gets the link to top up.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as api from "../../api/store";
import { useAuth } from "../../auth/AuthContext";
import { useT } from "../../i18n";

/** Warn from here on. Early enough to act, late enough not to nag. */
const WARN_AT = 0.8;

export function CreditsBanner() {
  const t = useT();
  const { user } = useAuth();
  const [credits, setCredits] = useState<api.CreditBalance | null>(null);

  const tenantId = user?.tenant_id ?? null;
  useEffect(() => {
    if (!tenantId) return;   // super-admins hold no credits
    let cancelled = false;
    api
      .myCredits()
      .then((c) => {
        if (!cancelled) setCredits(c);
      })
      // A balance we cannot read is not worth an error — the AI features
      // report their own failures.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  if (!credits) return null;

  const { balance, spent_this_month: spent, monthly_credits: monthly } = credits;
  const out = balance <= 0;
  // Only a school with a known monthly figure can be "80% through" it.
  const used = monthly > 0 ? spent / monthly : 0;
  if (!out && used < WARN_AT) return null;

  // A tenant_admin is the person who can act; everyone else is told who to ask.
  const canBuy = user?.role === "tenant_admin";
  const tone = out
    ? "border-danger/40 bg-danger/10 text-danger"
    : "border-warning/40 bg-warning/10 text-ink";

  const message = out
    ? canBuy ? t("credits.outAdmin") : t("credits.outLearner")
    : t("credits.low", { percent: Math.min(99, Math.round(used * 100)) });

  return (
    <div role="status" className={`mb-4 rounded-card border px-4 py-3 text-sm ${tone}`}>
      <span>{message}</span>
      {canBuy && (
        <>
          {" "}
          <Link to="/store" className="font-bold underline">
            {t("credits.topUp")}
          </Link>
        </>
      )}
    </div>
  );
}
